import React, { useState } from 'react';
import { X, Lock, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useTranslation } from 'react-i18next';

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isChanging, setIsChanging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  if (!isOpen) return null;

  const validatePassword = (password: string): string[] => {
    const errors: string[] = [];
    if (password.length < 8) errors.push(t('auth.change_password.requirements.min_length'));
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError(t('auth.change_password.errors.all_fields_required'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t('auth.change_password.errors.passwords_do_not_match'));
      return;
    }

    const passwordErrors = validatePassword(newPassword);
    if (passwordErrors.length > 0) {
      setError(`${t('auth.change_password.errors.invalid_password')}: ${passwordErrors.join(', ')}`);
      return;
    }

    if (currentPassword === newPassword) {
      setError(t('auth.change_password.errors.same_as_old'));
      return;
    }

    setIsChanging(true);

    try {
      // First, verify the current password by attempting to sign in with it
      const { data: user } = await supabase.auth.getUser();
      if (!user.user?.email) {
        throw new Error(t('auth.change_password.errors.no_user'));
      }

      // Verify current password by attempting to sign in
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.user.email,
        password: currentPassword,
      });

      if (signInError) {
        throw new Error(t('auth.change_password.errors.incorrect_current_password'));
      }

      // If current password is correct, proceed to update the password
      const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
      if (updateError) throw updateError;

      setSuccess(true);
      setTimeout(() => {
        onClose();
        setSuccess(false);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      }, 1500);
    } catch (err: any) {
      setError(err?.message || t('auth.change_password.errors.generic'));
    } finally {
      setIsChanging(false);
    }
  };

  const handleClose = () => {
    if (!isChanging) {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setError(null);
      setSuccess(false);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center">
            <Lock className="w-6 h-6 text-indigo-600 mr-3" />
            <div>
              <h2 className="text-xl font-bold text-gray-900">{t('auth.change_password.title')}</h2>
              <p className="text-gray-600 text-sm mt-1">{t('auth.change_password.subtitle')}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={isChanging}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {success ? (
            <div className="text-center py-8">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-green-900 mb-2">
                {t('auth.change_password.success_title')}
              </h3>
              <p className="text-green-700 text-sm">
                {t('auth.change_password.success_desc')}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center">
                  <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
                  <span className="text-red-700 text-sm">{error}</span>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('auth.change_password.current_password')}
                </label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder={t('auth.change_password.current_password_ph')}
                    required
                    disabled={isChanging}
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    disabled={isChanging}
                  >
                    {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('auth.change_password.new_password')}
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder={t('auth.change_password.new_password_ph')}
                    required
                    disabled={isChanging}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    disabled={isChanging}
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {newPassword && (
                  <div className="mt-2">
                    <div className="text-xs text-gray-600 mb-1">{t('auth.change_password.password_strength')}</div>
                    <div className="flex space-x-1">
                      {[1, 2, 3, 4].map((level) => {
                        const errors = validatePassword(newPassword);
                        const strength = 4 - errors.length;
                        return (
                          <div
                            key={level}
                            className={`h-1 flex-1 rounded ${level <= strength
                              ? strength === 1 ? 'bg-red-500'
                              : strength === 2 ? 'bg-orange-500'
                              : strength === 3 ? 'bg-yellow-500'
                              : 'bg-green-500'
                              : 'bg-gray-200'}`}
                          />
                        );
                      })}
                    </div>
                    {validatePassword(newPassword).length > 0 && (
                      <div className="text-xs text-red-600 mt-1">
                        {t('auth.change_password.required_prefix')}: {validatePassword(newPassword).join(', ')}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('auth.change_password.confirm_new_password')}
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder={t('auth.change_password.confirm_new_password_ph')}
                    required
                    disabled={isChanging}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    disabled={isChanging}
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {confirmPassword && (
                  <div className="mt-1 text-xs">
                    {newPassword === confirmPassword ? (
                      <span className="text-green-600 flex items-center">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        {t('auth.change_password.match_ok')}
                      </span>
                    ) : (
                      <span className="text-red-600 flex items-center">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        {t('auth.change_password.match_ko')}
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                <h4 className="font-medium text-blue-900 mb-1 text-sm">{t('auth.change_password.tips_title')}</h4>
                <ul className="text-xs text-blue-800 space-y-1">
                  <li>{t('auth.change_password.tips.0')}</li>
                  <li>{t('auth.change_password.tips.1')}</li>
                  <li>{t('auth.change_password.tips.2')}</li>
                  <li>{t('auth.change_password.tips.3')}</li>
                </ul>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isChanging}
                  className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors disabled:opacity-50"
                >
                  {t('auth.change_password.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isChanging || !currentPassword || !newPassword || !confirmPassword || newPassword !== confirmPassword || validatePassword(newPassword).length > 0}
                  className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isChanging ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      {t('auth.change_password.changing')}
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4 mr-2" />
                      {t('auth.change_password.submit')}
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordModal;


